import { useEffect, useRef, useState } from 'react';
import { LOCALES, localeMeta, useI18n, type Locale } from '../i18n';

/**
 * The flag button and its dropdown.
 *
 * The flag is the handle people scan for; the language's own name beside it is
 * what actually identifies it, since flags and languages do not line up (Arabic
 * has no country, English and Spanish have several). Both are shown in the
 * list, and the trigger carries an accessible name so the flag alone is never
 * the only label.
 */
export function LanguagePicker({ className }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const current = localeMeta(locale);

  // Tapping anywhere else, or pressing Escape, closes it — a dropdown that only
  // closes by re-tapping the trigger is a trap on a phone.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (next: Locale) => {
    setLocale(next);
    setOpen(false);
  };

  return (
    <div className={className ? `lang ${className}` : 'lang'} ref={boxRef}>
      <button
        type="button"
        className="lang-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t.language.choose} — ${t.language.current(current.name)}`}
      >
        <span className="lang-flag" aria-hidden="true">
          {current.flag}
        </span>
        {/* The code, always. Flag emoji are absent from Windows entirely and
            from any device without a colour emoji font, and a button that
            renders as an empty box tells the visitor nothing. Two letters cost
            almost no room and never fail. */}
        <span className="lang-code" aria-hidden="true">
          {current.code.toUpperCase()}
        </span>
      </button>

      {open && (
        <ul className="lang-menu" role="listbox" aria-label={t.language.choose}>
          {LOCALES.map((option) => (
            <li key={option.code}>
              <button
                type="button"
                className={
                  option.code === locale ? 'lang-option lang-option--active' : 'lang-option'
                }
                role="option"
                aria-selected={option.code === locale}
                onClick={() => choose(option.code)}
                // Each row reads in its own language, so the name is not
                // announced with the surrounding page's accent.
                lang={option.code}
                dir={option.dir}
              >
                <span className="lang-flag" aria-hidden="true">
                  {option.flag}
                </span>
                <span className="lang-name">{option.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
